import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from './components/ui/sonner';
import { 
  FileText, BarChart3, Inbox, GitBranch, Scale, Building2, 
  ListTodo, Layers, Search, LogOut, User, Settings, Users 
} from 'lucide-react';

// Auth
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoginPage from './pages/LoginPage';
import UserManagementPage from './pages/UserManagementPage';

// Pages
import DashboardPage from './pages/AnalyticsPage';  // Dashboard (analytics)
import QueuesPage from './pages/QueuesPage';  // Queues page
import WorkflowPage from './pages/WorkflowPage';  // Workflow page
import IngestPage from './pages/IngestPage';  // Ingest page
import ClassifyPage from './pages/ClassifyPage';  // Classify page
import DetectPage from './pages/DenialsPage';  // Detect page (formerly Denials)
import AppealsPage from './pages/AppealsPage';  // Appeal page
import AppealDetailPage from './pages/AppealDetailPage';
import PayerManagementPage from './pages/PayerManagementPage';  // Payers page
import DocumentReviewPage from './pages/DocumentReviewPage';

// Legacy redirect - keep old Extract page working
import LegacyExtractPage from './pages/DashboardPage';

import './App.css';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './components/ui/dropdown-menu';
import { Button } from './components/ui/button';
import { Avatar, AvatarFallback } from './components/ui/avatar';
import { Skeleton } from './components/ui/skeleton';

// Create QueryClient
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Protected Route wrapper
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="space-y-4 text-center">
          <Skeleton className="h-12 w-12 rounded-full mx-auto" />
          <Skeleton className="h-4 w-32 mx-auto" />
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

// Admin Route wrapper
function AdminRoute({ children }) {
  const { isAdmin, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="space-y-4 text-center">
          <Skeleton className="h-12 w-12 rounded-full mx-auto" />
          <Skeleton className="h-4 w-32 mx-auto" />
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function Navigation() {
  const location = useLocation();
  const { user, logout, isAdmin } = useAuth();

  const isActive = (path) => location.pathname === path;

  const handleLogout = async () => {
    await logout();
  };

  const getUserInitials = () => {
    if (!user?.full_name) return 'U';
    return user.full_name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  return (
    <nav className="bg-white border-b border-border">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center space-x-8">
            <div className="flex items-center space-x-2">
              <FileText className="w-8 h-8 text-primary" />
              <h1 className="text-2xl font-bold text-foreground">CLAIRVOY</h1>
              <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium">v2.0</span>
            </div>
            
            <div className="hidden md:flex items-center space-x-1">
              <Link
                to="/"
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                  isActive('/')
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
                data-testid="nav-dashboard"
              >
                <BarChart3 className="w-4 h-4" />
                <span className="text-sm font-medium">Dashboard</span>
              </Link>
              
              <Link
                to="/queues"
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                  isActive('/queues')
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
                data-testid="nav-queues"
              >
                <ListTodo className="w-4 h-4" />
                <span className="text-sm font-medium">Queues</span>
              </Link>
              
              <Link
                to="/workflow"
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                  isActive('/workflow')
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
                data-testid="nav-workflow"
              >
                <GitBranch className="w-4 h-4" />
                <span className="text-sm font-medium">Workflow</span>
              </Link>
              
              <Link
                to="/ingest"
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                  isActive('/ingest')
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
                data-testid="nav-ingest"
              >
                <Inbox className="w-4 h-4" />
                <span className="text-sm font-medium">Ingest</span>
              </Link>
              
              <Link
                to="/classify"
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                  isActive('/classify')
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
                data-testid="nav-classify"
              >
                <Layers className="w-4 h-4" />
                <span className="text-sm font-medium">Classify</span>
              </Link>
              
              <Link
                to="/detect"
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                  isActive('/detect')
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
                data-testid="nav-detect"
              >
                <Search className="w-4 h-4" />
                <span className="text-sm font-medium">Denials</span>
              </Link>
              
              <Link
                to="/appeal"
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                  isActive('/appeal')
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
                data-testid="nav-appeal"
              >
                <Scale className="w-4 h-4" />
                <span className="text-sm font-medium">Appeals</span>
              </Link>
              
              <Link
                to="/payers"
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                  isActive('/payers')
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
                data-testid="nav-payers"
              >
                <Building2 className="w-4 h-4" />
                <span className="text-sm font-medium">Payers</span>
              </Link>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <span className="text-xs text-muted-foreground hidden lg:block">AI-Powered EOB Processing</span>
            
            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-9 w-9 rounded-full" data-testid="user-menu-btn">
                  <Avatar className="h-9 w-9">
                    <AvatarFallback className="bg-primary/10 text-primary text-sm font-medium">
                      {getUserInitials()}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end" forceMount>
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">{user?.full_name}</p>
                    <p className="text-xs leading-none text-muted-foreground">{user?.email}</p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem disabled>
                  <User className="mr-2 h-4 w-4" />
                  <span>Profile</span>
                </DropdownMenuItem>
                {isAdmin && (
                  <DropdownMenuItem asChild>
                    <Link to="/users" className="flex items-center cursor-pointer">
                      <Users className="mr-2 h-4 w-4" />
                      <span>User Management</span>
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem disabled>
                  <Settings className="mr-2 h-4 w-4" />
                  <span>Settings</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive" data-testid="logout-btn">
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Log out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </nav>
  );
}

function Footer() {
  return (
    <footer className="fixed bottom-0 left-0 right-0 bg-white border-t border-border py-3 z-50">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <a 
          href="https://junolabs.ai" 
          target="_blank" 
          rel="noopener noreferrer"
          className="flex items-center justify-center space-x-3 hover:opacity-80 transition-opacity"
        >
          <span className="text-sm text-muted-foreground">Powered by</span>
          <img 
            src="/assets/rts_logo.png" 
            alt="RTS Logo" 
            className="h-8 object-contain"
          />
        </a>
      </div>
    </footer>
  );
}

function AuthenticatedApp() {
  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main className="pb-20">
        <Routes>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/queues" element={<QueuesPage />} />
          <Route path="/workflow" element={<WorkflowPage />} />
          <Route path="/ingest" element={<IngestPage />} />
          <Route path="/classify" element={<ClassifyPage />} />
          <Route path="/detect" element={<DetectPage />} />
          <Route path="/appeal" element={<AppealsPage />} />
          <Route path="/appeal/:appealId" element={<AppealDetailPage />} />
          <Route path="/payers" element={<PayerManagementPage />} />
          <Route path="/documents/:id" element={<DocumentReviewPage />} />
          <Route path="/users" element={
            <AdminRoute>
              <UserManagementPage />
            </AdminRoute>
          } />
          {/* Legacy routes for backward compatibility */}
          <Route path="/extract" element={<LegacyExtractPage />} />
          <Route path="/denials" element={<DetectPage />} />
          <Route path="/appeals" element={<AppealsPage />} />
          <Route path="/appeals/:appealId" element={<AppealDetailPage />} />
        </Routes>
      </main>
      <Footer />
    </div>
  );
}

function AppRoutes() {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="space-y-4 text-center">
          <div className="flex items-center justify-center space-x-2">
            <FileText className="w-10 h-10 text-primary animate-pulse" />
          </div>
          <p className="text-muted-foreground">Loading CLAIRVOY...</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={
        isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />
      } />
      <Route path="/*" element={
        <ProtectedRoute>
          <AuthenticatedApp />
        </ProtectedRoute>
      } />
    </Routes>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
          <AppRoutes />
          <Toaster />
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
